import { Button, Card, Table } from 'react-bootstrap';
import { FcCheckmark } from 'react-icons/fc';
import { BiEdit } from 'react-icons/bi';
import { TbMessageCircle2 } from 'react-icons/tb';
import { format } from 'date-fns';
import { isUpcomingActivity } from '@services/activity-tracker/activityTrackerService';
import {
  getFormattedShiftTimes,
  isShiftConfirmed,
} from '@services/shift/shiftService';
import { FaRegClock } from 'react-icons/fa';
import { useChatContext } from '@company/state/chatContext';
import { ActivityDetail } from '@th-types/daily-activity.type';
import IJobDetail, {
  JobAssignment,
  JobMember,
  ShiftPosition,
} from '@th-types/job.detail.type';
import { IJobStats, ShiftStatus } from '@th-types/jobs.type';
import DATE_FORMATS from '@constants/dateFormat';
import {
  getCheckInValue,
  getCheckOutValue,
  getStatusColumn,
} from '@utils/JobUtils';
import { isShiftPayable } from '@utils/ShiftsUtils';
import {
  convertToTimezone,
  getDateNowByTimezone,
  UTC_TIMEZONE,
} from '@utils/DateUtils';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import JobShiftsItemHeader from './JobShiftsItemHeader';

import './style.scss';

interface Props {
  job?: IJobDetail;
  jobStats?: IJobStats;
  jobShiftActivities: ActivityDetail[];
  showJobMembers: (members: JobMember[]) => void;
  openWorkerSideModal?: (
    workerId: number,
    jobAssignmentId?: number,
    jobId?: number
  ) => void;
  handleOpenModalShareJobLink: (job: IJobDetail) => void;
  handleMissingHourNoticed: (shiftPositionId: number) => void;
  handleMissingActivityNoticed: (shiftPositionId: number) => void;
  handleConfirmMarkPaid: (activityDetail: ActivityDetail) => void;
  handleOpenEditShiftModal: (shiftPosition: ShiftPosition) => void;
  handleOpenSubmitHoursModal: (activityDetail: ActivityDetail) => void;
  handleOpenReviewShiftModal: (activityDetail: ActivityDetail) => void;
  completeOnboarding: (jobAssignment: JobAssignment, complete: boolean) => void;
}

export default function JobShiftsTableItem({
  job,
  jobStats,
  jobShiftActivities,
  showJobMembers,
  completeOnboarding,
  openWorkerSideModal,
  handleConfirmMarkPaid,
  handleOpenEditShiftModal,
  handleMissingHourNoticed,
  handleOpenSubmitHoursModal,
  handleOpenReviewShiftModal,
  handleOpenModalShareJobLink,
  handleMissingActivityNoticed,
}: Props) {
  const { openChat } = useChatContext();
  const jobAssignmentStatus = useJobAssignmentStatus()(undefined);
  const { shouldShowWorkerInfoInChat } = jobAssignmentStatus;

  if (!jobStats || !job) {
    return null;
  }

  const TABLE_HEADER_WEB = [
    'WORKER',
    'SHIFT STATUS',
    'ONBOARDING',
    'SCHEDULED TIME',
    'CHECK IN',
    'CHECK OUT',
    'SUBMITTED TIME',
    'TOTAL HOURS',
    'WORK PAY',
    'TOTAL PAY',
    'SHIFT PAID',
  ];

  const getOnboardingButton = (activity: ActivityDetail) => {
    if (activity.shiftPosition.shiftStatus !== ShiftStatus.CONFIRMED) {
      return <div> - </div>;
    }
    if (!activity.shiftPosition.jobAssignment?.onBoardingCompleted) {
      return (
        <Button
          size="sm"
          variant="light"
          className="row-at-button"
          onClick={() =>
            activity.shiftPosition.jobAssignment &&
            completeOnboarding(activity.shiftPosition.jobAssignment, true)
          }
        >
          MARK COMPLETED
        </Button>
      );
    }
    return (
      <FcCheckmark
        color="var(--black)"
        className="cursor-point"
        onClick={() =>
          activity.shiftPosition.jobAssignment &&
          completeOnboarding(activity.shiftPosition.jobAssignment, false)
        }
      />
    );
  };

  const disableMarkPaid = (activity: ActivityDetail) => {
    return (
      !isShiftConfirmed(activity.shiftPosition) ||
      !activity.shiftPosition.jobAssignment ||
      isUpcomingActivity(activity, job.address.timezoneId || UTC_TIMEZONE) ||
      !isShiftPayable(job, activity)
    );
  };

  const getMarkPaidColumn = (activity: ActivityDetail) => {
    if (!isShiftConfirmed(activity.shiftPosition)) {
      return <div> - </div>;
    }
    if (activity.shiftPosition.isPaid) {
      return (
        <FcCheckmark
          className="cursor-point"
          onClick={() => {
            handleConfirmMarkPaid(activity);
          }}
        />
      );
    }

    return (
      <Button
        size="sm"
        variant="light"
        className="row-at-button"
        disabled={disableMarkPaid(activity)}
        onClick={() => handleConfirmMarkPaid(activity)}
      >
        MARK PAID
      </Button>
    );
  };

  const handleOpenWorkerSideModal = (activity: ActivityDetail) => {
    if (
      openWorkerSideModal &&
      activity.shiftPosition.jobAssignment?.worker.id
    ) {
      openWorkerSideModal(
        activity.shiftPosition.jobAssignment.worker.id,
        activity.shiftPosition.jobAssignment.id,
        activity.shiftPosition.jobId
      );
    }
  };

  const showSubmitHours = (activity: ActivityDetail) => {
    return (
      isShiftConfirmed(activity.shiftPosition) &&
      convertToTimezone(
        new Date(activity.shiftPosition.end),
        job.address.timezoneId
      ) < getDateNowByTimezone(job.address.timezoneId)
    );
  };

  return (
    <Card className="py-2 my-3 mx-4 job-shifts-card">
      <JobShiftsItemHeader
        job={job}
        jobStats={jobStats}
        showJobMembers={showJobMembers}
        handleOpenModalShareJobLink={handleOpenModalShareJobLink}
      />

      <Table borderless>
        <thead className="table-header fw-bold">
          <tr>
            {TABLE_HEADER_WEB.map((header, index) => (
              <th className={index === 0 ? 'text-start' : ''} key={header}>
                {header}
              </th>
            ))}
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody className="at-table-body">
          {jobShiftActivities.map((activity: ActivityDetail) => (
            <tr key={activity.shiftPosition.id}>
              <td
                className="text-start text-truncate"
                style={{ width: '180px' }}
              >
                <span
                  className="text-capitalize cursor-point at-name-link"
                  onClick={() => handleOpenWorkerSideModal(activity)}
                >
                  {activity.shiftPosition.jobAssignment?.worker.fullName}
                </span>
              </td>
              {getStatusColumn(job, activity, handleMissingHourNoticed)}
              <td>{getOnboardingButton(activity)}</td>
              <td>
                {getFormattedShiftTimes(
                  activity.shiftPosition,
                  job?.address?.timezoneId
                )}
              </td>
              <td>
                {getCheckInValue(
                  job,
                  activity,
                  handleOpenReviewShiftModal,
                  handleMissingActivityNoticed
                )}
              </td>
              <td>
                {getCheckOutValue(
                  job,
                  activity,
                  handleOpenReviewShiftModal,
                  handleMissingActivityNoticed
                )}
              </td>
              <td>
                {(activity.timeSheet?.localizedStart &&
                  activity.timeSheet?.localizedEnd && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenReviewShiftModal(activity)}
                    >
                      {`${format(
                        new Date(activity.timeSheet.localizedStart),
                        DATE_FORMATS.TIME_FORMAT
                      )} - ${format(
                        new Date(activity.timeSheet.localizedEnd),
                        DATE_FORMATS.TIME_FORMAT
                      )}`}
                    </Button>
                  )) ||
                  '-'}
              </td>
              <td>
                {activity.timeSheet?.hoursWorked
                  ? `${activity.timeSheet.hoursWorked}h`
                  : '-'}
              </td>
              <td>
                {activity.shiftPosition?.wage && (
                  <div>
                    {'$ '}
                    {Number(activity.shiftPosition.wage.payRate)}/
                    {activity.shiftPosition.wage.payRatePeriod.toLowerCase()}
                  </div>
                )}
              </td>
              <td>
                {(activity.timeSheet?.workerEarnedMoney && (
                  <div>
                    {'$ '}
                    {Number(activity.timeSheet?.workerEarnedMoney).toFixed(2)}
                  </div>
                )) ||
                  '-'}
              </td>
              <td>{getMarkPaidColumn(activity)}</td>
              <td style={{ textAlign: 'end' }}>
                {activity.shiftPosition.shiftStatus !== ShiftStatus.OPEN && (
                  <TbMessageCircle2
                    color="var(--black)"
                    size={28}
                    className="cursor-point pe-1"
                    onClick={() => {
                      if (activity.shiftPosition.jobAssignment) {
                        openChat(
                          activity.shiftPosition.jobAssignment.worker,
                          activity.shiftPosition.jobAssignment.id,
                          shouldShowWorkerInfoInChat({
                            status: activity.shiftPosition.jobAssignment.status,
                          })
                        );
                      }
                    }}
                  />
                )}

                {!activity.timeSheet && showSubmitHours(activity) && (
                  <FaRegClock
                    color="var(--black)"
                    size={20}
                    className="cursor-point"
                    onClick={() => handleOpenSubmitHoursModal(activity)}
                  />
                )}

                <BiEdit
                  color="var(--black)"
                  size={28}
                  className="cursor-point ps-1"
                  aria-label={`Review ${activity.shiftPosition.jobAssignment?.worker.fullName}`}
                  onClick={() => {
                    if (
                      activity.shiftPosition.shiftStatus ===
                        ShiftStatus.CONFIRMED &&
                      (activity.dailyActivity || activity.timeSheet)
                    ) {
                      handleOpenReviewShiftModal(activity);
                    } else {
                      handleOpenEditShiftModal(activity.shiftPosition);
                    }
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
